import type {
  KpiTile,
  WidgetInstance,
  WidgetPayload
} from "../../../../components/widgets/types"
import type {
  CashFlow,
  DashboardCatalogEntry,
  DashboardTile,
  DashboardTileValue,
  NbaItem,
  PortfolioBundle,
  ProjectStatus
} from "../../redux/portfolio.types"

export interface ResolvedWidget {
  widget: WidgetInstance
  payload: WidgetPayload
}

type AnyTile = DashboardTile | DashboardTileValue

export function kpiWidget(tile: AnyTile, currency?: string): ResolvedWidget {
  return {
    widget: {
      id: tile.id,
      type: "kpi",
      dataSource: tile.category,
      title: tile.title,
      drill_to: tile.drill_to,
      config: { metric: tile.id },
      capabilities: []
    },
    payload: {
      tile: tile as KpiTile,
      currency,
      no_access: (tile as { no_access?: boolean }).no_access
    }
  }
}

export function placeholderWidget(
  entry: DashboardCatalogEntry,
  currency?: string
): ResolvedWidget {
  const tile: KpiTile = {
    id: entry.id,
    title: entry.title,
    value: "—",
    unit: entry.unit,
    drill_to: "",
    description: entry.description,
    formula: entry.formula,
    basis: entry.basis
  }
  return {
    widget: {
      id: entry.id,
      type: "kpi",
      dataSource: entry.category,
      title: entry.title,
      drill_to: "",
      config: { metric: entry.id },
      capabilities: []
    },
    payload: { tile, currency }
  }
}

export function cashFlowWidget(cashFlow?: CashFlow): ResolvedWidget {
  return {
    widget: {
      id: "cash_flow",
      type: "chart",
      dataSource: "financial",
      config: { visualisation: "composed", metric: "cash_flow_series" },
      capabilities: []
    },
    payload: {
      chart: {
        visualisation: "composed",
        cashFlow: {
          series: cashFlow?.series ?? [],
          currency: cashFlow?.currency,
          granularity: cashFlow?.granularity,
          asOf: cashFlow?.as_of
        }
      }
    }
  }
}

export function projectStatusWidget(status: ProjectStatus): ResolvedWidget {
  return {
    widget: {
      id: "project_status",
      type: "chart",
      dataSource: "projects",
      config: { visualisation: "donut", metric: "project_status_breakdown" },
      capabilities: []
    },
    payload: { chart: { visualisation: "donut", status } }
  }
}

export function feedWidget(items: NbaItem[]): ResolvedWidget {
  return {
    widget: { id: "nba", type: "feed", dataSource: "nba", capabilities: [] },
    payload: { feed: { items } }
  }
}

export function bundleToWidgetInstances(
  bundle: PortfolioBundle
): WidgetInstance[] {
  return [
    ...bundle.tiles.map((tile) => kpiWidget(tile).widget),
    cashFlowWidget(bundle.cash_flow).widget,
    projectStatusWidget(bundle.project_status).widget,
    feedWidget(bundle.nba.items).widget
  ]
}

export function resolveKpiPayload(
  bundle: PortfolioBundle,
  id: string,
  currency?: string
): WidgetPayload {
  const tile = bundle.tiles.find((t) => t.id === id) ?? bundle.all_values?.[id]
  if (tile) return kpiWidget(tile, currency).payload

  const entry = bundle.catalog?.find((c) => c.id === id)
  if (entry) return placeholderWidget(entry, currency).payload

  return {
    tile: { id, title: id, value: "—", drill_to: "" },
    currency
  }
}
